import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Clock, LogIn, LogOut, Coffee, Check, ChevronLeft, ChevronRight, AlertCircle, X as Close, Lock } from "lucide-react";

const Y_HERO = "/yasamaru/hero.png";
const Y_CHEER = "/yasamaru/cheer.png";
const Y_BREAK = "/yasamaru/break.png";
const Y_TENKI = "/yasamaru/tenki.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build a fresh axios instance that always sends the saved terminal token
function makeClient(slug) {
  const inst = axios.create({ baseURL: API });
  inst.interceptors.request.use((config) => {
    const tok = typeof window !== "undefined" ? localStorage.getItem(`term_token_${slug}`) : null;
    if (tok) config.headers["X-Terminal-Token"] = tok;
    return config;
  });
  return inst;
}

function statusLabel(s) {
  return { off: "未出勤", working: "勤務中", on_break: "休憩中", done: "退勤済み" }[s] || "—";
}
function statusColor(s) {
  return {
    off: "bg-stone-50 border-stone-200 text-stone-500",
    working: "bg-emerald-50 border-emerald-300 text-emerald-700",
    on_break: "bg-amber-50 border-amber-300 text-amber-700",
    done: "bg-stone-100 border-stone-300 text-stone-500",
  }[s] || "bg-stone-50";
}

export default function SharedPunch() {
  const { slug } = useParams();
  const [pub] = useState(() => makeClient(slug));
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [unlocked, setUnlocked] = useState(() => !!(typeof window !== "undefined" && localStorage.getItem(`term_token_${slug}`)));
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    try {
      const { data } = await pub.get(`/punch/${slug}/staff`);
      setData(data);
      setErr("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 401 || detail === "terminal_unlock_required") {
        localStorage.removeItem(`term_token_${slug}`);
        setUnlocked(false);
        setErr("");
      } else {
        setErr(detail || e.message);
      }
    }
  };

  useEffect(() => {
    if (!unlocked) return;
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [slug, unlocked]);

  const punch = async (type) => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const { data: res } = await pub.post(`/punch/${slug}`, { user_id: selected.id, type });
      const msg = { in: "出勤しました！", out: "おつかれさまでした！", break_start: "休憩開始", break_end: "休憩終了" }[type];
      setToast({ name: res.name, message: msg, ok: true });
      setTimeout(() => { setSelected(null); setToast(null); load(); }, 1800);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 401 || detail === "terminal_unlock_required") {
        localStorage.removeItem(`term_token_${slug}`);
        setUnlocked(false);
        setSelected(null);
        setToast(null);
        return;
      }
      setToast({ name: selected.name, message: detail || e.message, ok: false });
      setTimeout(() => setToast(null), 2500);
    } finally { setBusy(false); }
  };

  const fmt2 = (n) => String(n).padStart(2, "0");
  const timeStr = `${fmt2(clock.getHours())}:${fmt2(clock.getMinutes())}:${fmt2(clock.getSeconds())}`;
  const dateStr = `${clock.getFullYear()}年${clock.getMonth() + 1}月${clock.getDate()}日 (${"日月火水木金土"[clock.getDay()]})`;

  if (!unlocked) {
    return <UnlockScreen slug={slug} onUnlocked={() => setUnlocked(true)} />;
  }

  if (err) {
    return (
      <div className="min-h-screen bg-soft-mint flex items-center justify-center p-6">
        <div className="card-soft p-8 max-w-md text-center">
          <AlertCircle className="text-rose-500 mx-auto" size={36} />
          <div className="font-display font-black text-xl text-stone-900 mt-3">{err}</div>
          <div className="text-stone-500 text-sm mt-2">URLが正しいかご確認ください。</div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-stone-500">読み込み中...</div>;

  return (
    <div className="min-h-screen bg-soft-mint relative overflow-hidden">
      <div className="blob bg-emerald-200" style={{ width: 380, height: 380, top: -80, right: -80 }} />
      <div className="blob bg-amber-200" style={{ width: 260, height: 260, bottom: -60, left: -60 }} />

      <header className="relative z-10 px-5 md:px-8 py-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <img src={Y_HERO} alt="やさまる" className="w-12 h-12" />
          <div>
            <div className="font-display font-black text-xl text-stone-900" data-testid="tenant-name">{data.tenant.name}</div>
            <div className="text-xs text-stone-500">勤怠打刻 ・ やさまるタイム</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display tabular-nums font-black text-3xl text-stone-900" data-testid="live-clock">{timeStr}</div>
          <div className="text-xs text-stone-500">{dateStr}</div>
        </div>
      </header>

      <main className="relative z-10 px-5 md:px-8 pb-10">
        {!selected ? (
          <StaffGrid items={data.items} onSelect={setSelected} />
        ) : (
          <PunchPanel staff={selected} onBack={() => setSelected(null)} onPunch={punch} busy={busy} pub={pub} />
        )}
      </main>

      <div className="relative z-10 px-5 md:px-8 pb-6 text-center">
        <button
          onClick={() => { localStorage.removeItem(`term_token_${slug}`); setUnlocked(false); setSelected(null); }}
          data-testid="terminal-lock-btn"
          className="text-xs text-stone-400 hover:text-stone-700 inline-flex items-center gap-1">
          <Lock size={12} /> この端末をロック
        </button>
      </div>

      {toast && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4 pointer-events-none" data-testid="punch-toast">
          <div className={`pointer-events-auto card-soft p-6 md:p-8 max-w-md w-full text-center ${toast.ok ? "bg-emerald-50 border-2 border-emerald-300" : "bg-rose-50 border-2 border-rose-300"}`}>
            <img src={toast.ok ? Y_CHEER : Y_TENKI} alt="やさまる" className="w-24 mx-auto" />
            <div className="font-display font-black text-2xl text-stone-900 mt-2">{toast.name}さん</div>
            <div className={`mt-1 font-bold ${toast.ok ? "text-emerald-700" : "text-rose-700"}`}>{toast.message}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function UnlockScreen({ slug, onUnlocked }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/punch/${slug}/unlock`, { passcode: code });
      localStorage.setItem(`term_token_${slug}`, data.token);
      onUnlocked();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "認証に失敗しました");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-soft-mint relative overflow-hidden flex items-center justify-center p-6">
      <div className="blob bg-emerald-200" style={{ width: 320, height: 320, top: -60, right: -60 }} />
      <div className="blob bg-amber-200" style={{ width: 220, height: 220, bottom: -50, left: -50 }} />
      <form onSubmit={submit} className="relative z-10 card-soft p-8 md:p-10 max-w-md w-full text-center" data-testid="unlock-form">
        <img src={Y_HERO} alt="やさまる" className="w-16 h-16 mx-auto" />
        <div className="font-display font-black text-2xl text-stone-900 mt-3">打刻端末のロック解除</div>
        <p className="text-sm text-stone-600 mt-2 leading-relaxed">この端末で打刻を行うには、<br />管理者から共有された<strong>6桁のパスコード</strong>を入力してください。</p>
        <input
          autoFocus
          data-testid="unlock-passcode-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          autoComplete="off"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="••••••"
          className="mt-5 w-full text-center font-display font-black text-4xl tracking-[0.4em] tabular-nums px-4 py-4 rounded-2xl border-2 border-stone-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none bg-white"
        />
        {err && <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="unlock-error">{err}</div>}
        <button
          disabled={busy || code.length < 4}
          data-testid="unlock-submit-btn"
          className="mt-5 w-full rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-black text-lg py-3.5">
          {busy ? "認証中..." : "ロックを解除"}
        </button>
        <div className="mt-5 text-xs text-stone-500 leading-relaxed">
          パスコードは管理画面の<strong>「設定」</strong>タブで確認・再発行できます。<br />
          一度認証すれば、この端末では次回から入力不要です。
        </div>
      </form>
    </div>
  );
}

function StaffGrid({ items, onSelect }) {
  if (items.length === 0) {
    return (
      <div className="card-soft p-10 text-center max-w-md mx-auto">
        <div className="font-display font-bold text-stone-700">まだ従業員が登録されていません</div>
        <div className="text-stone-500 text-sm mt-1">管理者がスタッフを追加してください。</div>
      </div>
    );
  }
  return (
    <div>
      <h2 className="font-display text-lg md:text-xl font-black text-stone-900 mb-3">名前を選んでください</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="staff-grid">
        {items.map((s) => (
          <button key={s.id} onClick={() => onSelect(s)} data-testid={`staff-${s.id}`}
            className="rounded-3xl border-2 bg-white border-stone-200 hover:border-emerald-300 hover:bg-emerald-50/30 p-5 md:p-6 text-center hover:scale-[1.02] hover:shadow-lg transition-all active:scale-95 min-h-[88px] flex items-center justify-center">
            <div className="font-display font-black text-stone-900 text-lg md:text-xl">{s.name}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const c = { off: "bg-stone-400", working: "bg-emerald-500", on_break: "bg-amber-500", done: "bg-stone-400" }[status] || "bg-stone-400";
  return <span className={`inline-block w-2 h-2 rounded-full ${c}`} />;
}

function PunchPanel({ staff, onBack, onPunch, busy, pub }) {
  const { slug } = useParams();
  const [showMonth, setShowMonth] = useState(false);
  const canIn = staff.status === "off";
  const canOut = staff.status === "working" || staff.status === "on_break";
  const canBreakStart = staff.status === "working";
  const canBreakEnd = staff.status === "on_break";

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onBack} data-testid="back-to-staff-btn" className="text-stone-500 hover:text-stone-800 text-sm inline-flex items-center gap-1 mb-3">
        <ChevronLeft size={14} /> 戻る
      </button>
      <div className="card-soft p-6 md:p-8 text-center">
        <div className="text-xs text-stone-500">本日の打刻</div>
        <div className="font-display font-black text-3xl md:text-4xl text-stone-900 mt-1" data-testid="punch-staff-name">{staff.name} さん</div>
        <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1 text-sm font-bold ${statusColor(staff.status)}`}>
          <StatusDot status={staff.status} /> {statusLabel(staff.status)}
        </div>

        <div className="grid grid-cols-2 gap-3 md:gap-4 mt-6">
          <BigBtn icon={<LogIn size={22} />} label="出勤" color="emerald" onClick={() => onPunch("in")} disabled={!canIn || busy} testid="punch-in" />
          <BigBtn icon={<LogOut size={22} />} label="退勤" color="rose" onClick={() => onPunch("out")} disabled={!canOut || busy} testid="punch-out" />
          <BigBtn icon={<Coffee size={22} />} label="休憩開始" color="amber" onClick={() => onPunch("break_start")} disabled={!canBreakStart || busy} testid="punch-break-start" />
          <BigBtn icon={<Check size={22} />} label="休憩終了" color="indigo" onClick={() => onPunch("break_end")} disabled={!canBreakEnd || busy} testid="punch-break-end" />
        </div>

        {staff.status === "done" && (
          <div className="mt-5 text-sm text-stone-500 inline-flex items-center gap-1">
            <Check size={14} className="text-emerald-500" /> 本日はおつかれさまでした
          </div>
        )}

        <button onClick={() => setShowMonth(true)} data-testid="open-staff-monthly-btn"
          className="mt-6 text-emerald-700 hover:text-emerald-800 text-sm font-semibold inline-flex items-center gap-1 underline">
          📅 今月の出勤状況を見る
        </button>
      </div>

      {showMonth && <MonthlyModal slug={slug} staff={staff} onClose={() => setShowMonth(false)} pub={pub} />}
    </div>
  );
}

function MonthlyModal({ slug, staff, onClose, pub }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    pub.get(`/punch/${slug}/staff/${staff.id}/monthly`, { params: { year, month } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [slug, staff.id, year, month, pub]);

  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  const totalMin = items.reduce((s, r) => s + durMin(r), 0);
  const workedDays = items.filter((r) => r.clock_in).length;

  return (
    <div className="fixed inset-0 z-40 bg-stone-900/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="staff-monthly-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full my-8 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between sticky top-0 bg-white rounded-t-3xl">
          <div>
            <div className="text-xs text-stone-500">{staff.name}さん の出勤状況</div>
            <div className="flex items-center gap-2 mt-1">
              <button onClick={prev} className="rounded-full p-1.5 hover:bg-stone-100"><ChevronLeft size={16} /></button>
              <span className="font-display font-black text-xl">{year}年{month}月</span>
              <button onClick={next} className="rounded-full p-1.5 hover:bg-stone-100"><ChevronRight size={16} /></button>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-monthly-modal" className="rounded-full p-2 hover:bg-stone-100 text-stone-500"><Close size={18} /></button>
        </div>

        <div className="px-6 py-3 border-b border-stone-100 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2">
            <div className="text-[10px] text-emerald-700">稼働日数</div>
            <div className="font-display text-2xl font-black text-stone-900 tabular-nums">{workedDays}日</div>
          </div>
          <div className="rounded-xl bg-indigo-50 border border-indigo-200 px-3 py-2">
            <div className="text-[10px] text-indigo-700">総労働時間</div>
            <div className="font-display text-2xl font-black text-stone-900 tabular-nums">{fmtHM(totalMin)}</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center text-stone-500 py-10">読み込み中...</div>
          ) : items.length === 0 ? (
            <div className="text-center text-stone-500 py-10">この月の打刻はまだありません</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-stone-500">
                <tr><th className="text-left py-2">日付</th><th className="text-left">出勤</th><th className="text-left">退勤</th><th className="text-left">休憩</th><th className="text-left">実労働</th></tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.date} className="border-t border-stone-100">
                    <td className="py-2 font-semibold">{r.date}</td>
                    <td className="text-emerald-700 tabular-nums">{fmtTime(r.clock_in?.at)}</td>
                    <td className="text-rose-700 tabular-nums">{fmtTime(r.clock_out?.at)}</td>
                    <td className="tabular-nums">{(r.breaks || []).length}回</td>
                    <td className="tabular-nums font-semibold">{fmtHM(durMin(r))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tokyo" }); } catch { return "—"; }
}
function durMin(r) {
  if (!r?.clock_in?.at || !r?.clock_out?.at) return 0;
  let ms = new Date(r.clock_out.at) - new Date(r.clock_in.at);
  for (const b of r.breaks || []) {
    if (b.start?.at && b.end?.at) ms -= new Date(b.end.at) - new Date(b.start.at);
  }
  return Math.max(0, Math.round(ms / 60000));
}
function fmtHM(m) { const h = Math.floor(m/60); const mm = m%60; return `${h}時間${String(mm).padStart(2,"0")}分`; }

function BigBtn({ icon, label, color, onClick, disabled, testid }) {
  const palette = {
    emerald: "bg-emerald-500 hover:bg-emerald-600 text-white",
    rose: "bg-rose-500 hover:bg-rose-600 text-white",
    amber: "bg-amber-500 hover:bg-amber-600 text-white",
    indigo: "bg-indigo-500 hover:bg-indigo-600 text-white",
  }[color];
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`rounded-3xl ${palette} font-display font-black text-xl md:text-2xl py-7 md:py-9 px-4 inline-flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95 shadow-lg`}>
      {icon}{label}
    </button>
  );
}
